import React, { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { sortingFns } from "@tanstack/react-table";
import { compareItems } from "@tanstack/match-sorter-utils";
import MasterTable from "../../../components/master/MasterTable";
import { contractAddIcon, downloadIcon } from "../../../assetData/icons";
import { actionPerm } from "../../../components/authNavigation/AuthNavbar";
import { sortData } from "../../../utils/sortData";
import { FaSortDown, FaSortUp } from "react-icons/fa";
import { downloadLoader } from "../../../assetData/loaders";

const fuzzySort = (rowA, rowB, columnId) => {
  console.log("functioning... fuzzySort");
  let dir = 0;

  // Only sort by rank if the column has ranking information
  if (rowA.columnFiltersMeta[columnId]) {
    dir = compareItems(
      rowA.columnFiltersMeta[columnId]?.itemRank,
      rowB.columnFiltersMeta[columnId]?.itemRank,
    );
  }

  // Provide an alphanumeric fallback for when the item ranks are equal
  return dir === 0 ? sortingFns.alphanumeric(rowA, rowB, columnId) : dir;
};

const MasterFnyearTable = (props) => {
  const {
    tableData,
    buttonConfig,
    setIsAdd,
    setIsEdit,
    setIsDelete,
    setSelectedItemId,
    renderKey,
    setGlobalFilters,
    loading,
    actionPermissions,
    name,
    isDownloadClicked,
  } = props;

  const [tableFilter, setTableFilter] = useState(tableData);
  const [sortingType, setSortingType] = useState("asc");
  const [sortColumn, setSortColumn] = useState("fin_year_name");

  useEffect(() => {
    setTableFilter(tableData);
  }, [tableData]);

  const handleSorting = (column, sortType, type) => {
    setSortColumn(column);

    const sortedData = sortData({
      data: tableData,
      column,
      type,
      sortType,
    });

    setTableFilter(sortedData);
    setSortingType(sortingType === "asc" ? "desc" : "asc");
  };

  const columns = React.useMemo(
    () => [
      {
        accessorKey: "sl",
        header: () => <span>S. NO.</span>,
        cell: (info) => <span>{info.row.index + 1}.</span>,
      },

      {
        accessorFn: (row) => row.fin_year_name,
        id: "fin_year_name",
        cell: (info) => info.getValue(),
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "fin_year_name",
                sortingType === "asc" ? "desc" : "asc",
                "string",
              );
            }}
            className=" flex justify-between"
          >
            <div>FINANCIAL YEAR NAME</div>
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "fin_year_name" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "fin_year_name" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        filterFn: "fuzzy",
        sortingFn: fuzzySort,
      },

      {
        accessorFn: (row) => row.fin_year_short_name,
        id: "fin_year_short_name",
        cell: (info) => info.getValue(),
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "fin_year_short_name",
                sortingType === "asc" ? "desc" : "asc",
                "string",
              );
            }}
            className=" flex justify-between"
          >
            <div>SHORT NAME</div>
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "fin_year_short_name" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "fin_year_short_name" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
      },

      {
        accessorKey: "eff_start_date",
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "eff_start_date",
                sortingType === "asc" ? "desc" : "asc",
                "date",
              );
            }}
            className="flex justify-between "
          >
            START DATE{" "}
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "eff_start_date" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "eff_start_date" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) =>
          info.getValue() && format(new Date(info.getValue()), "dd MMM yyyy"),
      },
      {
        accessorKey: "eff_end_date",
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "eff_end_date",
                sortingType === "asc" ? "desc" : "asc",
                "date",
              );
            }}
            className="flex justify-between "
          >
            END DATE{" "}
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "eff_end_date" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "eff_end_date" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) =>
          info.getValue() && format(new Date(info.getValue()), "dd MMM yyyy"),
      },
      {
        accessorKey: "status",
        header: () => <span>STATUS</span>,
        cell: (info) => (
          <span className="tracking-widest">{info.getValue()}</span>
        ),
      },

      {
        accessorKey: "id",
        header: () => <div className="flex justify-center">ACTION</div>,
        cell: (info) => <div className="flex justify-center"> </div>,
      },
    ],
    [sortColumn, sortingType, tableData, tableFilter],
  );

  const handleDownloadButtonClick = () => {
    console.log("Download button clicked!"); // Add this line
    buttonConfig.downloadHandler();
  };

  const actionConfig = [
    {
      id: "1",
      name: "Add Fin Year",
      image: contractAddIcon,
      actionHandler: () => setIsAdd(true),
      disable: actionPerm(actionPermissions)?.add_perm,
    },

    {
      id: "2",
      name: "Download Data",
      image: isDownloadClicked ? downloadLoader : downloadIcon,
      actionHandler: isDownloadClicked
        ? () => {}
        : buttonConfig.downloadHandler,
      disable: actionPerm(actionPermissions)?.download_perm,
    },
  ];

  const action = actionPerm(actionPermissions);

  // let name = "Add Fin Year";

  return (
    <MasterTable
      name={name}
      columns={columns}
      tableData={tableFilter?.length > 0 ? tableFilter : tableData}
      buttonConfig={buttonConfig}
      setIsAdd={setIsAdd}
      setIsEdit={setIsEdit}
      setIsDelete={setIsDelete}
      setSelectedItemId={setSelectedItemId}
      renderKey={renderKey}
      loading={loading}
      filterField
      actionConfig={actionConfig}
      setGlobalFilters={setGlobalFilters}
      handleDownloadButtonClick={handleDownloadButtonClick}
      actions={action}
    />
  );
};

export default MasterFnyearTable;
